import React from "react";
import { Heart } from "lucide-react";
import { useSavedHomes } from "../lib/savedHomes";
import { track } from "../lib/tracking";

// Reusable Save / Unsave heart button. Two visual variants:
//   variant="overlay"  — circular pill on top of imagery (light)
//   variant="solid"    — solid card-style chip used in lighter contexts
// Both honour reduced-motion and high-contrast accessibility modes via
// CSS classes on <html>.

export default function SaveHomeButton({
  slug,
  homeName = "this home",
  variant = "overlay",
  size = "md",
  className = "",
  testid,
}) {
  const { isSaved, toggle } = useSavedHomes();
  const saved = isSaved(slug);

  const onClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    toggle(slug);
    track(saved ? "save_home_remove" : "save_home_add", { slug });
  };

  const dim = size === "sm" ? "w-8 h-8" : size === "lg" ? "w-11 h-11" : "w-10 h-10";
  const iconDim = size === "sm" ? "w-4 h-4" : size === "lg" ? "w-5 h-5" : "w-4.5 h-4.5";

  if (variant === "overlay") {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-pressed={saved}
        aria-label={saved ? `Remove ${homeName} from saved homes` : `Save ${homeName} to your shortlist`}
        data-testid={testid || `save-home-${slug}`}
        className={`${dim} rounded-full backdrop-blur-md border transition-all flex items-center justify-center shadow-lg focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/40 ${
          saved
            ? "bg-[color:var(--alpha-purple-700)] border-[color:var(--alpha-purple-800)] text-white hover:bg-[color:var(--alpha-purple-800)]"
            : "bg-white/95 border-white/70 text-[color:var(--alpha-purple-700)] hover:bg-white"
        } ${className}`}
      >
        <Heart className={`${iconDim} ${saved ? "fill-current" : ""}`} aria-hidden />
        <span className="sr-only">{saved ? "Saved" : "Save"}</span>
      </button>
    );
  }

  // "solid" / chip variant
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={saved}
      aria-label={saved ? `Remove ${homeName} from saved homes` : `Save ${homeName} to your shortlist`}
      data-testid={testid || `save-home-${slug}`}
      className={`inline-flex items-center gap-2 rounded-full px-4 py-2.5 border text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[color:var(--alpha-purple-300)] ${
        saved
          ? "bg-[color:var(--alpha-purple-700)] text-white border-[color:var(--alpha-purple-800)] hover:bg-[color:var(--alpha-purple-800)]"
          : "bg-white text-[color:var(--alpha-purple-700)] border-[color:var(--alpha-purple-200)] hover:bg-[color:var(--alpha-purple-50)]"
      } ${className}`}
    >
      <Heart className={`w-4 h-4 ${saved ? "fill-current" : ""}`} aria-hidden />
      {saved ? "Saved" : "Save home"}
    </button>
  );
}
